import { Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { ModifierInput, ModifierService } from '@lendiodevs/lendio-js-utilities/dist/modifiers';
import { Store } from '@ngxs/store';
import { AchFunctionalApprovalSample } from 'ng2-app/components/modifiers/data/ach-approval.sample';
import {
  AchMaxPaymentFunctionalApprovalSample
} from 'ng2-app/components/modifiers/data/ach-maxPayment-approval.sample';
import { LocFunctionalApprovalSample } from 'ng2-app/components/modifiers/data/loc-approval.sample';
import { TermFunctionalApprovalSample } from 'ng2-app/components/modifiers/data/term-approval.sample';
import { ModifierInputComponent } from 'ng2-app/components/modifiers/modifier-input.component';
import {
  DynamicScreenHeightModule
} from 'ng2-app/modules/directives/dynamic-screen-height/dynamic-screen-height.module';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { AddStackableNotificationSuccess, AddStackableNotificationError } from 'ng2-app/store/stackable-notifications/StackableNotifications.actions';

type ModifierInputFormGroup = {
  base: FormControl<any>;
  value: FormControl<any>;
}

type ModifiersFormGroup = {
  sampleData: FormControl<string | null>;
  inputs: FormArray<FormGroup<ModifierInputFormGroup>>;
}

@Component({
  selector: 'app-modifiers',
  imports: [
    DynamicScreenHeightModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    ModifierInputComponent,
    MatButton,
    MatDivider,
    MatOption,
    MatSelect,
  ],
  providers: [
    ModifierService,
  ],
  template: `
    @if( canManageModifers() ) {
      <section appDynamicScreenHeight class="column-grid tw-grid-cols-[30%_1fr_1fr] p-4 tw-gap-4">
        <section>

          <mat-form-field class="!tw-block">
            <mat-label>Select Sample Data</mat-label>
            <mat-select [formControl]="sampleDataTypeFormControl" (selectionChange)="setSampleData($event)">
              @for (option of sampleOptions; track $index) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field class="!tw-block">
            <mat-label>Sample Input Data</mat-label>
            <textarea matInput
                      cdkTextareaAutosize
                      #autosize="cdkTextareaAutosize"
                      cdkAutosizeMinRows="15"
                      cdkAutosizeMaxRows="30"
                      [formControl]="sampleDataFormControl">
          </textarea>
          </mat-form-field>

        </section>

        <section class="tw-grid tw-gap-2 tw-h-fit">
          <h2>Inputs</h2>
          @for (input of formGroup.controls.inputs.controls; track $index) {
            <modifier-input [fg]="input"/>
            <mat-divider/>
          }

          <button mat-stroked-button (click)="add()">Add</button>
        </section>

        <section class="tw-grid tw-gap-2 tw-h-fit">
          <div class="column-grid tw-justify-between tw-place-items-center">

            <h3>Results</h3>
            <button mat-stroked-button (click)="evaluate()">
              Test
            </button>

          </div>
          <div>
            {{ results || 'N\A' }}
          </div>
          <mat-divider/>
          <div class="column-grid tw-justify-between tw-place-items-center">
            <h3>Inputs JSON</h3>
            <button mat-flat-button [color]="'primary'" (click)="copy()">
              Copy
            </button>
          </div>
          <div>
            {{ inputs() }}
          </div>
        </section>
      </section>
    }
  `,
})
export class ModifiersComponent {

  canManageModifers = this.store.selectSignal( LenderUserState.hasPermission('internal:manageModifiers') );
  sampleOptions = [
    {
      label: 'ACH Approval',
      value: AchFunctionalApprovalSample,
    },
    {
      label: 'ACH Max Payment Approval',
      value: AchMaxPaymentFunctionalApprovalSample,
    },
    {
      label: 'Term Approval',
      value: TermFunctionalApprovalSample,
    },
    {
      label: 'LOC Approval',
      value: LocFunctionalApprovalSample,
    },
  ];

  sampleDataTypeFormControl = new FormControl<any>(null);
  sampleDataFormControl = new FormControl<any>(null);

  //@ts-ignore
  formGroup: FormGroup<ModifiersFormGroup> = new FormGroup({
    inputs: new FormArray([]),
  });

  results: any;
  fgChanges = toSignal( this.formGroup.valueChanges );
  inputs = computed(() => {
    const fg = this.fgChanges();
    return JSON.stringify(this.formGroup.controls.inputs.value);
  });

  constructor(
    private modifierService: ModifierService,
    private store: Store,
  ) {}

  add() {
    this.formGroup.controls.inputs.push(this.createModifierInput());
  }

  createModifierInput() {
    return new FormGroup({
      base: new FormControl('inputParams', Validators.required),  // Assuming base is a string
      value: new FormControl('attributesMap.'),
    });
  }

  setSampleData($event) {
    this.sampleDataFormControl.setValue(JSON.stringify($event.value));
  }

  evaluate() {
    const inputs = this.formGroup.controls.inputs.value as unknown as ModifierInput[];
    this.results = this.modifierService.reduceInputs(
      inputs,
      JSON.parse(this.sampleDataFormControl.value),
      {},
      {},
    );
  }

  async copy() {
    const inputs = JSON.stringify(this.formGroup.controls.inputs.value);
    try {
      await navigator.clipboard.writeText(inputs);
      this.store.dispatch(new AddStackableNotificationSuccess({
        title: 'Inputs copied to clipboard',
      }));
    } catch (e) {
      this.store.dispatch(new AddStackableNotificationError({
        title: 'Failed to copy inputs to clipboard',
      }));
    }

  }
}
